import type { $Fetch } from "ofetch";
import { pdfUrl } from "~/utils/apiUrl";

const PDF_TOKEN_CACHE_KEY = "pdf-sanctum-token";
const PDF_TOKEN_EXPIRY_KEY = "pdf-sanctum-token-expires";

// Refresh 30s before actual expiry to avoid edge-case failures
const TOKEN_REFRESH_MARGIN_MS = 30 * 1000;

// In-flight request deduplication (transient, not persisted)
let pendingTokenRequest: Promise<string> | null = null;

function getCachedToken(): string | null {
  return sessionStorage.getItem(PDF_TOKEN_CACHE_KEY);
}

function getTokenExpiry(): number {
  return Number(sessionStorage.getItem(PDF_TOKEN_EXPIRY_KEY) || 0);
}

function isTokenValid(): boolean {
  const token = getCachedToken();
  return token !== null && Date.now() < getTokenExpiry() - TOKEN_REFRESH_MARGIN_MS;
}

/**
 * Récupère un token PDF en cache (sessionStorage), ou en demande un nouveau si expiré.
 * Les appels concurrents partagent la même requête en vol (déduplication).
 */
async function fetchPdfToken(client: $Fetch, tokenLifetimeMs: number): Promise<string> {
  if (isTokenValid()) {
    return getCachedToken()!;
  }

  // Deduplicate: if a request is already in-flight, reuse it
  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }

  pendingTokenRequest = client(pdfUrl.getToken)
    .then((token: string) => {
      sessionStorage.setItem(PDF_TOKEN_CACHE_KEY, token);
      sessionStorage.setItem(PDF_TOKEN_EXPIRY_KEY, String(Date.now() + tokenLifetimeMs));
      return token;
    })
    .finally(() => {
      pendingTokenRequest = null;
    });

  return pendingTokenRequest;
}

/**
 * Invalide le token en cache (ex: après une erreur 401).
 */
export function invalidatePdfToken(): void {
  sessionStorage.removeItem(PDF_TOKEN_CACHE_KEY);
  sessionStorage.removeItem(PDF_TOKEN_EXPIRY_KEY);
}

/**
 * Composable pour créer un client Sanctum pour le service PDF.
 * Le token est mis en cache et partagé entre tous les appelants.
 */
export const usePdfSanctumClient = (): $Fetch => {
  const config = useRuntimeConfig();
  const client = useSanctumClient();
  const tokenLifetimeMs = Number(config.public.pdfTokenExpirationMinutes) * 60 * 1000;

  return async (url: string, options: any = {}) => {
    const pdfToken = await fetchPdfToken(client, tokenLifetimeMs);

    const fullUrl = `${config.public.pdfServiceUrl}/api${url}`;

    return await $fetch(fullUrl, {
      ...options,
      headers: {
        ...options.headers,
        Accept: "application/json",
        Authorization: `Bearer ${pdfToken}`,
      },
    });
  };
};

/**
 * Récupère un token PDF valide (avec cache et déduplication).
 * À utiliser dans les composables qui gèrent eux-mêmes l'appel HTTP.
 */
export const usePdfToken = () => {
  const config = useRuntimeConfig();
  const client = useSanctumClient();
  const tokenLifetimeMs = Number(config.public.pdfTokenExpirationMinutes) * 60 * 1000;

  return {
    getToken: () => fetchPdfToken(client, tokenLifetimeMs),
    invalidate: invalidatePdfToken,
  };
};
